import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Button, ButtonGroup, Table, Form, Modal, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import apiService from '../services/api';

const Offerings = () => {
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOffering, setEditingOffering] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [saving, setSaving] = useState(false);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [selectedOffering, setSelectedOffering] = useState(null);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Filter state
  const [statusFilter, setStatusFilter] = useState('active');
  const [billingCycleFilter, setBillingCycleFilter] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    erp_account_code: '',
    max_users: '',
    description: '',
    billing_cycle_id: '',
    currency_id: '',
    country_id: '',
    billing_cycle_charge: '',
    setup_charge: '',
    features: '[]',
    isActive: true,
    enforced_user_limit: false
  });

  // Features editor state: structured solution/feature groups, editable as a form or raw JSON
  const [featureGroups, setFeatureGroups] = useState([]);
  const [featuresMode, setFeaturesMode] = useState('form');

  // Lookup data state
  const [billingCycles, setBillingCycles] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [solutionFeatures, setSolutionFeatures] = useState({}); // solution code -> available features
  const requestedSolutionFeatures = useRef(new Set());
  const [loadingLookupData, setLoadingLookupData] = useState(false);

  useEffect(() => {
    loadOfferings();
  }, [currentPage, itemsPerPage, statusFilter, billingCycleFilter]);

  // Load lookup data when component mounts
  useEffect(() => {
    loadLookupData();
  }, []);

  const loadLookupData = async () => {
    setLoadingLookupData(true);
    try {
      // Fetch billing cycles
      const billingCyclesResponse = await apiService.getBillingCycles();
      if (billingCyclesResponse.success) {
        setBillingCycles(billingCyclesResponse.data || []);
      }

      // Fetch currencies
      const currenciesResponse = await apiService.getCurrencies();
      if (currenciesResponse.success) {
        setCurrencies(currenciesResponse.data || []);
      }

      // Fetch countries
      const countriesResponse = await apiService.getCountries();
      if (countriesResponse.success) {
        setCountries(countriesResponse.data || []);
      }

      // Fetch solutions
      const solutionsResponse = await apiService.get('/solution');
      setSolutions(solutionsResponse.data || []);
    } catch (error) {
      console.error('Error loading lookup data:', error);
      setError('Failed to load lookup data');
    } finally {
      setLoadingLookupData(false);
    }
  };

  const loadOfferings = async () => {
    setLoading(true);
    setError('');

    try {
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (billingCycleFilter) filters.billing_cycle_id = billingCycleFilter;
      const response = await apiService.getOfferings(currentPage, itemsPerPage, filters);
      if (response.success) {
        console.log('Offerings data received:', response.data);
        setOfferings(response.data || []);
        if (response.pagination) {
          setTotalRecords(response.pagination.total_records || 0);
          setTotalPages(response.pagination.total_pages || 1);
        } else {
          const count = (response.data || []).length;
          setTotalRecords(count);
          setTotalPages(Math.max(1, Math.ceil(count / itemsPerPage)));
        }
      } else {
        setError(response.error || 'Failed to load offerings');
      }
    } catch (error) {
      console.error('Error loading offerings:', error);
      setError('Failed to load offerings');
    } finally {
      setLoading(false);
    }
  };

  // Normalizes to [{ solution: '<code>', features: ['<code>', ...] }].
  // Also accepts the detailed shape where solution/features are objects with a `code`.
  const toFeatureGroups = (value) => {
    if (!Array.isArray(value)) return [];
    const toCode = (item) => (typeof item === 'string' ? item : item?.code || '');
    return value.map(group => ({
      solution: toCode(group.solution),
      features: (group.features || []).map(toCode).filter(Boolean)
    }));
  };

  const serializeFeatureGroups = (groups) =>
    groups.map(({ features, solution }) => ({ features, solution }));

  // Load the available features for each selected solution (once per solution)
  useEffect(() => {
    featureGroups.forEach(({ solution: code }) => {
      const solution = solutions.find(s => s.code === code);
      if (!solution || requestedSolutionFeatures.current.has(code)) return;
      requestedSolutionFeatures.current.add(code);
      apiService.get(`/feature?solution_id=${solution.id}`)
        .then(response => setSolutionFeatures(prev => ({ ...prev, [code]: response.data || [] })))
        .catch(err => {
          console.error('Error loading features for solution:', code, err);
          requestedSolutionFeatures.current.delete(code);
        });
    });
  }, [featureGroups, solutions]);

  const addSolution = () => {
    setFeatureGroups(prev => [...prev, { solution: '', features: [] }]);
  };

  const removeSolution = (groupIndex) => {
    setFeatureGroups(prev => prev.filter((_, i) => i !== groupIndex));
  };

  const selectSolution = (groupIndex, code) => {
    setFeatureGroups(prev => prev.map((group, i) =>
      i === groupIndex ? { solution: code, features: [] } : group
    ));
  };

  const toggleFeature = (groupIndex, featureCode, checked) => {
    setFeatureGroups(prev => prev.map((group, i) => {
      if (i !== groupIndex) return group;
      const features = checked
        ? [...group.features, featureCode]
        : group.features.filter(code => code !== featureCode);
      return { ...group, features };
    }));
  };

  // Catalog features for a group, plus any saved feature codes no longer in the catalog
  const getFeatureOptions = (group) => {
    const catalog = solutionFeatures[group.solution] || [];
    const extras = group.features.filter(code => !catalog.some(c => c.code === code));
    return [...catalog, ...extras.map(code => ({ code, notInCatalog: true }))];
  };

  const handleFeaturesModeChange = (mode) => {
    if (mode === featuresMode) return;
    if (mode === 'json') {
      setFormData(prev => ({ ...prev, features: JSON.stringify(serializeFeatureGroups(featureGroups), null, 2) }));
    } else {
      let parsed;
      try {
        parsed = JSON.parse(formData.features);
      } catch {
        setError('Invalid JSON in Features field');
        return;
      }
      if (!Array.isArray(parsed)) {
        setError('Features JSON must be an array of solutions to use the form editor');
        return;
      }
      setFeatureGroups(toFeatureGroups(parsed));
    }
    setError('');
    setFeaturesMode(mode);
  };

  const handleShowModal = (offering = null) => {
    setFeaturesMode('form');
    if (offering) {
      setEditingOffering(offering);
      const savedFeatures = Array.isArray(offering.features) ? offering.features : offering.features_detailed;
      setFeatureGroups(toFeatureGroups(savedFeatures));
      setFormData({
        name: offering.name || '',
        code: offering.code || '',
        erp_account_code: offering.erp_account_code || '',
        max_users: offering.max_users || '',
        description: offering.description || '',
        billing_cycle_id: offering.billing_cycle_id || '',
        currency_id: offering.currency_id || '',
        country_id: offering.country_id || '',
        billing_cycle_charge: offering.billing_cycle_charge || '',
        setup_charge: offering.setup_charge || '',
        features: JSON.stringify(serializeFeatureGroups(toFeatureGroups(savedFeatures)), null, 2),
        isActive: offering.is_active !== false,
        enforced_user_limit: offering.enforced_user_limit === true
      });
    } else {
      setEditingOffering(null);
      setFeatureGroups([]);
      setFormData({
        name: '',
        code: '',
        erp_account_code: '',
        max_users: '',
        description: '',
        billing_cycle_id: '',
        currency_id: '',
        country_id: '',
        billing_cycle_charge: '',
        setup_charge: '',
        features: '[]',
        isActive: true,
        enforced_user_limit: false
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOffering(null);
    setFormData({
      name: '',
      code: '',
      erp_account_code: '',
      max_users: '',
      description: '',
      billing_cycle_id: '',
      currency_id: '',
      country_id: '',
      billing_cycle_charge: '',
      setup_charge: '',
      features: '[]',
      isActive: true,
      enforced_user_limit: false
    });
    setFeatureGroups([]);
    setFeaturesMode('form');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateJSON = (jsonString) => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validate JSON
    if (featuresMode === 'json' && !validateJSON(formData.features)) {
      setError('Invalid JSON in Features field');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const offeringData = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        erp_account_code: formData.erp_account_code.trim(),
        max_users: formData.max_users !== '' ? parseInt(formData.max_users, 10) : null,
        description: formData.description.trim(),
        billing_cycle_id: formData.billing_cycle_id,
        currency_id: formData.currency_id,
        country_id: formData.country_id,
        billing_cycle_charge: formData.billing_cycle_charge ? parseFloat(formData.billing_cycle_charge) : null,
        setup_charge: formData.setup_charge ? parseFloat(formData.setup_charge) : null,
        features: featuresMode === 'json'
          ? JSON.parse(formData.features)
          : serializeFeatureGroups(featureGroups),
        is_active: formData.isActive,
        enforced_user_limit: formData.enforced_user_limit === true
      };

      let response;
      if (editingOffering) {
        response = await apiService.updateOffering(editingOffering.id, offeringData);
      } else {
        response = await apiService.createOffering(offeringData);
      }

      if (response.success) {
        handleCloseModal();
        await loadOfferings();
      } else {
        setError(response.error || 'Failed to save offering');
      }
    } catch (error) {
      console.error('Error saving offering:', error);
      setError('Failed to save offering');
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleViewFeatures = (offering) => {
    setSelectedOffering(offering);
    setShowFeaturesModal(true);
  };

  const handleCloseFeaturesModal = () => {
    setShowFeaturesModal(false);
    setSelectedOffering(null);
  };

  // Sorting functionality
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="bi bi-arrow-down-up text-muted ms-1"></i>;
    }
    return sortConfig.direction === 'asc'
      ? <i className="bi bi-arrow-up text-primary ms-1"></i>
      : <i className="bi bi-arrow-down text-primary ms-1"></i>;
  };

  // Filter and sort offerings based on current configuration
  const sortedOfferings = React.useMemo(() => {
    let sortableOfferings = [...offerings];
    if (sortConfig.key) {
      sortableOfferings.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle different data types
        switch (sortConfig.key) {
          case 'name':
          case 'code':
            aValue = (aValue || '').toString().toLowerCase();
            bValue = (bValue || '').toString().toLowerCase();
            break;
          case 'is_active':
            aValue = aValue ? 1 : 0;
            bValue = bValue ? 1 : 0;
            break;
          case 'created_at':
          case 'last_updated_at':
            aValue = new Date(aValue || 0);
            bValue = new Date(bValue || 0);
            break;
          case 'billing_cycle_charge':
            aValue = parseFloat(aValue || 0);
            bValue = parseFloat(bValue || 0);
            break;
          default:
            aValue = aValue || '';
            bValue = bValue || '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableOfferings;
  }, [offerings, sortConfig]);

  return (
    <div>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-box-seam me-2"></i>
              Offerings
            </h2>
            <Button variant="primary" onClick={() => handleShowModal()}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Offering
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Filter Section */}
      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Status Filter</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="">All Offerings</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Billing Cycle Filter</Form.Label>
            <Form.Select
              value={billingCycleFilter}
              onChange={(e) => { setBillingCycleFilter(e.target.value); setCurrentPage(1); }}
              disabled={loadingLookupData}
            >
              <option value="">All Billing Cycles</option>
              {billingCycles.map((cycle) => (
                <option key={cycle.id} value={cycle.id}>
                  {cycle.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <Badge bg="secondary" className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
                  {totalRecords} Total Records
                </Badge>
                <div className="d-flex align-items-center gap-2">
                  <Form.Label className="mb-0 text-muted">Show:</Form.Label>
                  <Form.Select
                    size="sm"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    style={{ width: 'auto' }}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </Form.Select>
                  <span className="text-muted">per page</span>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : offerings.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-box-seam display-1"></i>
                  <div className="mt-2">No offerings found</div>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('name')}
                      >
                        Offering
                        {getSortIcon('name')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('code')}
                      >
                        Code
                        {getSortIcon('code')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('erp_account_code')}
                      >
                        ERP Account
                        {getSortIcon('erp_account_code')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('country_name')}
                      >
                        Country
                        {getSortIcon('country_name')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('billing_cycle_name')}
                      >
                        Billing Cycle
                        {getSortIcon('billing_cycle_name')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('currency_code')}
                      >
                        Currency
                        {getSortIcon('currency_code')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('billing_cycle_charge')}
                      >
                        Cyclic Payment
                        {getSortIcon('billing_cycle_charge')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('setup_charge')}
                      >
                        Setup (One Off) Payment
                        {getSortIcon('setup_charge')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('is_active')}
                      >
                        Status
                        {getSortIcon('is_active')}
                      </th>
                      <th>Features</th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('created_at')}
                      >
                        Created
                        {getSortIcon('created_at')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('last_updated_at')}
                      >
                        Updated
                        {getSortIcon('last_updated_at')}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOfferings.map((offering) => (
                      <tr key={offering.id}>
                        <td>
                          <div className="fw-medium">{offering.name}</div>
                          {offering.description && (
                            <div className="text-muted" style={{ fontSize: '0.85em', fontStyle: 'italic' }}>
                              {offering.description}
                            </div>
                          )}
                        </td>
                        <td>
                          <code>{offering.code}</code>
                        </td>
                        <td>
                          {offering.erp_account_code ? <span>{offering.erp_account_code}</span> : <span className="text-muted">N/A</span>}
                        </td>
                        <td>
                          {offering.country_name}
                        </td>
                        <td>
                          {offering.billing_cycle_name}
                        </td>
                        <td>
                          {offering.currency_code}
                        </td>
                        <td>
                          {offering.billing_cycle_charge ?
                            <span className="fw-medium text-success">KES {parseFloat(offering.billing_cycle_charge).toLocaleString()}</span> :
                            <span className="text-muted">No billing cycle charge set</span>
                          }
                        </td>
                        <td>
                          {offering.setup_charge ?
                            <span className="fw-medium text-success">KES {parseFloat(offering.setup_charge).toLocaleString()}</span> :
                            <span className="text-muted">No setup charge set</span>
                          }
                        </td>
                        <td>
                          <Badge bg={offering.is_active ? 'success' : 'secondary'}>
                            {offering.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-info"
                            size="sm"
                            title="View Features"
                            onClick={() => handleViewFeatures(offering)}
                          >
                            <i className="bi bi-eye"></i> View Features
                          </Button>
                        </td>
                        <td>{apiService.formatDate(offering.created_at)}</td>
                        <td>{apiService.formatDate(offering.last_updated_at)}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleShowModal(offering)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>

            {/* Pagination */}
            {totalPages > 1 && (() => {
              const maxVisible = 5;
              let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
              let end = Math.min(totalPages, start + maxVisible - 1);
              if (end - start + 1 < maxVisible) {
                start = Math.max(1, end - maxVisible + 1);
              }
              const totalShown = totalRecords;
              const firstRecord = totalShown === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1;
              const lastRecord = Math.min(currentPage * itemsPerPage, totalShown);

              return (
                <Card.Footer>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                      Showing {firstRecord} to {lastRecord} of {totalShown} records
                    </div>
                    <Pagination className="mb-0">
                      <Pagination.First
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                      />
                      <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                      {start > 1 && <Pagination.Ellipsis disabled />}
                      {Array.from({ length: end - start + 1 }, (_, i) => start + i).map((page) => (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Pagination.Item>
                      ))}
                      {end < totalPages && <Pagination.Ellipsis disabled />}
                      <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                      <Pagination.Last
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                      />
                    </Pagination>
                  </div>
                </Card.Footer>
              );
            })()}
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingOffering ? 'Edit Offering' : 'Add Offering'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {loadingLookupData && (
              <div className="text-center p-3">
                <Spinner animation="border" role="status" size="sm" className="me-2" />
                <span>Loading lookup data...</span>
              </div>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Offering Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Code *</Form.Label>
                  <Form.Control
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ERP Account</Form.Label>
                  <Form.Control
                    type="text"
                    name="erp_account_code"
                    value={formData.erp_account_code}
                    onChange={handleInputChange}
                    placeholder="Enter ERP Account Code"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Maximum Users</Form.Label>
                  <Form.Control
                    type="number"
                    name="max_users"
                    value={formData.max_users}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="Max Users"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter offering description..."
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Country *</Form.Label>
                  <Form.Select
                    name="country_id"
                    value={formData.country_id}
                    onChange={handleInputChange}
                    required
                    disabled={loadingLookupData}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Billing Cycle *</Form.Label>
                  <Form.Select
                    name="billing_cycle_id"
                    value={formData.billing_cycle_id}
                    onChange={handleInputChange}
                    required
                    disabled={loadingLookupData}
                  >
                    <option value="">Select Billing Cycle</option>
                    {billingCycles.map((cycle) => (
                      <option key={cycle.id} value={cycle.id}>
                        {cycle.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency *</Form.Label>
                  <Form.Select
                    name="currency_id"
                    value={formData.currency_id}
                    onChange={handleInputChange}
                    required
                    disabled={loadingLookupData}
                  >
                    <option value="">Select Currency</option>
                    {currencies.map((currency) => (
                      <option key={currency.id} value={currency.id}>
                        {currency.code} - {currency.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Billing Cycle Payment</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    name="billing_cycle_charge"
                    value={formData.billing_cycle_charge}
                    onChange={handleInputChange}
                    placeholder="0.00"
                  />
                  <Form.Text className="text-muted">
                    The amount charged per billing cycle
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Setup (One Off) Payment</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    name="setup_charge"
                    value={formData.setup_charge}
                    onChange={handleInputChange}
                    placeholder="0.00"
                  />
                  <Form.Text className="text-muted">
                    One-time setup fee charged upon subscription
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="d-flex align-items-center gap-4">
                  <Form.Check
                    type="checkbox"
                    name="isActive"
                    id="isActive"
                    label="Active Offering"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex align-items-center gap-4">
                  <Form.Check
                    type="checkbox"
                    name="enforced_user_limit"
                    id="enforced_user_limit"
                    label="User Limit Enforced"
                    checked={formData.enforced_user_limit}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
            </Row>
            <Form.Group className="mb-3 mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Solutions & Features *</Form.Label>
                <ButtonGroup size="sm">
                  <Button
                    variant={featuresMode === 'form' ? 'primary' : 'outline-primary'}
                    onClick={() => handleFeaturesModeChange('form')}
                  >
                    <i className="bi bi-ui-checks me-1"></i>Form
                  </Button>
                  <Button
                    variant={featuresMode === 'json' ? 'primary' : 'outline-primary'}
                    onClick={() => handleFeaturesModeChange('json')}
                  >
                    <i className="bi bi-braces me-1"></i>JSON
                  </Button>
                </ButtonGroup>
              </div>

              {featuresMode === 'form' ? (
                <>
                  {featureGroups.length === 0 && (
                    <div className="text-muted text-center border rounded py-3 mb-2">
                      No solutions added yet
                    </div>
                  )}
                  {featureGroups.map((group, groupIndex) => {
                    const takenCodes = featureGroups
                      .filter((_, i) => i !== groupIndex)
                      .map(g => g.solution);
                    const catalogSolution = solutions.find(s => s.code === group.solution);
                    const solutionInCatalog = Boolean(catalogSolution);
                    const featuresLoaded = Boolean(solutionFeatures[group.solution]);
                    const featureOptions = getFeatureOptions(group);
                    return (
                      <Card key={groupIndex} className="mb-3">
                        <Card.Header className="bg-light">
                          <Row className="g-2 align-items-center">
                            <Col md={11}>
                              <Form.Select
                                size="sm"
                                value={group.solution}
                                onChange={(e) => selectSolution(groupIndex, e.target.value)}
                                required
                                disabled={loadingLookupData}
                              >
                                <option value="">Select Solution</option>
                                {group.solution && !solutionInCatalog && (
                                  <option value={group.solution}>
                                    {group.solution} (not in catalog)
                                  </option>
                                )}
                                {solutions
                                  .filter(s => !takenCodes.includes(s.code))
                                  .map(s => (
                                    <option key={s.id} value={s.code}>
                                      {s.name} ({s.code})
                                    </option>
                                  ))}
                              </Form.Select>
                            </Col>
                            <Col md={1} className="text-end">
                              <Button
                                size="sm"
                                variant="outline-danger"
                                title="Remove solution"
                                onClick={() => removeSolution(groupIndex)}
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </Col>
                          </Row>
                          {catalogSolution?.description && (
                            <small className="text-muted d-block mt-1">{catalogSolution.description}</small>
                          )}
                        </Card.Header>
                        {group.solution && (
                          <Card.Body className="py-2">
                            {solutionInCatalog && !featuresLoaded ? (
                              <small className="text-muted">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading features...
                              </small>
                            ) : featureOptions.length === 0 ? (
                              <small className="text-muted">No features available for this solution</small>
                            ) : (
                              featureOptions.map(feature => (
                                <Form.Check
                                  key={feature.code}
                                  type="checkbox"
                                  id={`feature-${groupIndex}-${feature.code}`}
                                  checked={group.features.includes(feature.code)}
                                  onChange={(e) => toggleFeature(groupIndex, feature.code, e.target.checked)}
                                  label={
                                    <>
                                      {!feature.notInCatalog && `${feature.name} `}
                                      <code className="small">{feature.code}</code>
                                      {feature.notInCatalog && (
                                        <Badge bg="warning" text="dark" className="ms-2">Not in catalog</Badge>
                                      )}
                                      {feature.description && (
                                        <small className="text-muted d-block">{feature.description}</small>
                                      )}
                                    </>
                                  }
                                />
                              ))
                            )}
                          </Card.Body>
                        )}
                      </Card>
                    );
                  })}
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={addSolution}
                    disabled={featureGroups.length >= solutions.length}
                  >
                    <i className="bi bi-plus-lg me-1"></i>Add Solution
                  </Button>
                </>
              ) : (
                <>
                  <Form.Control
                    as="textarea"
                    rows={12}
                    name="features"
                    value={formData.features}
                    onChange={handleInputChange}
                    required
                    className="font-monospace"
                  />
                  <Form.Text className="text-muted">
                    An array of {'{ "features": ["<feature code>", ...], "solution": "<solution code>" }'} objects
                  </Form.Text>
                </>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Offering'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Features View Modal */}
      <Modal show={showFeaturesModal} onHide={handleCloseFeaturesModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-list-check me-2"></i>
            Features for {selectedOffering?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOffering && (
            <div>
              {/* Offering Details */}
              <Row className="mb-4">
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <h6 className="card-title">
                        <i className="bi bi-info-circle me-2"></i>
                        Offering Details
                      </h6>
                      <div className="mb-2">
                        <strong>Name:</strong> {selectedOffering.name}
                      </div>
                      <div className="mb-2">
                        <strong>Code:</strong> <code>{selectedOffering.code}</code>
                      </div>
                      <div className="mb-2">
                        <strong>Status:</strong>{' '}
                        <Badge bg={selectedOffering.is_active ? 'success' : 'secondary'}>
                          {selectedOffering.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      {selectedOffering.description && (
                        <div className="mb-2">
                          <strong>Description:</strong> {selectedOffering.description}
                        </div>
                      )}
                      {selectedOffering.billing_cycle_charge && (
                        <div className="mb-2">
                          <strong>Price:</strong> KES. {selectedOffering.billing_cycle_charge}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <h6 className="card-title">
                        <i className="bi bi-graph-up me-2"></i>
                        Summary
                      </h6>
                      <div className="mb-2">
                        <strong>Solutions:</strong> {selectedOffering.solution_count || 'N/A'}
                      </div>
                      <div className="mb-2">
                        <strong>Total Features:</strong> {selectedOffering.total_feature_count || 'N/A'}
                      </div>
                      <div className="mb-2">
                        <strong>Created:</strong> {apiService.formatDate(selectedOffering.created_at)}
                      </div>
                      <div className="mb-2">
                        <strong>Last Updated:</strong> {apiService.formatDate(selectedOffering.last_updated_at)}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Features by Solution */}
              {selectedOffering.features_detailed && Array.isArray(selectedOffering.features_detailed) ? (
                <div>
                  <h6 className="mb-3">
                    <i className="bi bi-collection me-2"></i>
                    Solutions & Features
                  </h6>
                  {selectedOffering.features_detailed.map((solutionGroup, index) => (
                    <Card key={index} className="mb-3">
                      <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">
                              <i className="bi bi-puzzle me-2"></i>
                              {solutionGroup.solution?.name || 'Unknown Solution'}
                            </h6>
                            <small className="text-muted">
                              Code: <code>{solutionGroup.solution?.code || 'N/A'}</code>
                            </small>
                          </div>
                          <Badge bg="primary" pill>
                            {solutionGroup.features?.length || 0} features
                          </Badge>
                        </div>
                        {solutionGroup.solution?.description && (
                          <div className="mt-2">
                            <small className="text-muted">{solutionGroup.solution.description}</small>
                          </div>
                        )}
                      </Card.Header>
                      <Card.Body>
                        {solutionGroup.features && solutionGroup.features.length > 0 ? (
                          <div className="row">
                            {solutionGroup.features.map((feature, featureIndex) => (
                              <div key={featureIndex} className="col-md-6 mb-3">
                                <div className="border rounded p-3 h-100">
                                  <div className="d-flex align-items-start">
                                    <i className="bi bi-check-circle text-success me-2 mt-1"></i>
                                    <div>
                                      <div className="fw-medium">{feature.name || 'Unnamed Feature'}</div>
                                      <small className="text-muted d-block mb-1">
                                        Code: <code>{feature.code || 'N/A'}</code>
                                      </small>
                                      {feature.description && (
                                        <small className="text-muted">{feature.description}</small>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center text-muted py-3">
                            <i className="bi bi-info-circle me-2"></i>
                            No features available for this solution
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <Card.Body>
                    <div className="text-center text-muted py-4">
                      <i className="bi bi-exclamation-triangle display-4"></i>
                      <div className="mt-2">No detailed features available for this offering</div>
                      {selectedOffering.features && (
                        <div className="mt-3">
                          <small className="text-muted">Raw features data:</small>
                          <pre className="bg-light p-2 mt-2 text-start" style={{ fontSize: '0.8em' }}>
                            {JSON.stringify(selectedOffering.features, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseFeaturesModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Offerings;
